/**
 * Editor de **piezas a cortar** (réplica de `renderPartsTable` del SSR).
 *
 * Tabla controlada de piezas: color, nombre, ancho, largo, cantidad y rotación, con
 * acciones de duplicar/eliminar y un botón de agregar. Presentacional: recibe las
 * piezas y los callbacks del orquestador (`CutlistScreen`), no toca la API ni mantiene
 * el estado (`docs/frontend_architecture.md`, Pr. 6). Los números se normalizan a un
 * mínimo positivo al teclear, como en el SSR.
 *
 * La tabla se **compacta** (padding de celda reducido, inputs numéricos angostos y
 * acciones como botones de icono) para que sus 7 columnas quepan dentro de la sidebar
 * sin activar el scroll horizontal del wrapper de `ui/Table` (que es compartido y no se
 * toca). El ensanche de la sidebar vive en `CutlistScreen.tsx`.
 *
 * Los inputs numéricos (ancho/largo/cantidad) **ocultan los spinners nativos** de
 * `type=number` y se marcan `min-w-0`. Sin esto el `Input` base (`block w-full`) NO se
 * deja encoger por debajo de su ancho mínimo intrínseco (dígitos + spinners ~16-20px):
 * la columna se ensancha, el valor se corta ("60" → "6") y se desborda la tabla,
 * activando el `overflow-x-auto` del `ui/Table`. El `Input` base es compartido, así que
 * el ajuste se hace por `className` aquí, no en la librería (feature #78).
 */
import {
  Button,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui";

import { cm2ToM2 } from "../format";
import type { Part } from "../types";

interface PartsEditorProps {
  parts: Part[];
  onUpdate: (id: number, patch: Partial<Part>) => void;
  onAdd: () => void;
  onRemove: (id: number) => void;
  onDuplicate: (id: number) => void;
}

/**
 * Padding horizontal compacto para celdas (la librería `ui/Table` aplica `px-4`; al
 * pasar `px-*` por `className`, `cn()` deja ganar a esta clase). Mantiene `py-2` para no
 * pegar las filas verticalmente.
 */
const COMPACT_CELL = "px-1.5 py-2";

/**
 * Oculta los spinners (flechitas) nativos de `<input type=number>`. `appearance:textfield`
 * (Firefox) + los pseudo-elementos `::-webkit-{outer,inner}-spin-button` (Chrome/Safari)
 * con `appearance:none` y `margin:0`. Sin ellos el control reserva ~16-20px y, en una
 * columna estrecha, deja espacio para un solo dígito ("60" se ve "6").
 */
const NO_SPINNER =
  "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none " +
  "[&::-webkit-inner-spin-button]:appearance-none [&::-webkit-inner-spin-button]:m-0";

/**
 * Inputs numéricos: sin padding lateral excesivo, alineados a la derecha, sin spinners y
 * con `min-w-0` para que `w-full` (del `Input` base) pueda encoger dentro de la celda en
 * vez de empujar la columna y desbordar la tabla.
 */
const NUM_INPUT = `px-1.5 text-right tabular-nums min-w-0 ${NO_SPINNER}`;
/** Input de texto del nombre: padding compacto y `min-w-0` para ceder/ocupar el sobrante. */
const NAME_INPUT = "px-2 min-w-0";

export function PartsEditor({
  parts,
  onUpdate,
  onAdd,
  onRemove,
  onDuplicate,
}: PartsEditorProps) {
  const totalQty = parts.reduce((s, p) => s + Number(p.quantity || 0), 0);
  const totalArea = parts.reduce(
    (s, p) => s + p.width * p.height * Number(p.quantity || 0),
    0,
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-slate-500">
        <span>
          {parts.length} tipo{parts.length !== 1 ? "s" : ""} de pieza
        </span>
        <span>
          {totalQty} unidades · {cm2ToM2(totalArea, 3)} m² total
        </span>
      </div>

      <Table>
        <TableHead>
          <TableRow>
            <TableHeaderCell className={`${COMPACT_CELL} w-8`}>
              <span className="sr-only">Color</span>
            </TableHeaderCell>
            <TableHeaderCell className={COMPACT_CELL}>Nombre</TableHeaderCell>
            <TableHeaderCell className={`${COMPACT_CELL} w-20 text-right`}>
              Ancho
            </TableHeaderCell>
            <TableHeaderCell className={`${COMPACT_CELL} w-20 text-right`}>
              Largo
            </TableHeaderCell>
            <TableHeaderCell className={`${COMPACT_CELL} w-16 text-right`}>
              Cant.
            </TableHeaderCell>
            <TableHeaderCell
              className={`${COMPACT_CELL} w-8 text-center`}
              title="Rotación permitida"
            >
              ↻
            </TableHeaderCell>
            <TableHeaderCell className={`${COMPACT_CELL} w-16`}>
              <span className="sr-only">Acciones</span>
            </TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {parts.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="py-4 text-center text-slate-500">
                No hay piezas. Agrega la primera debajo.
              </TableCell>
            </TableRow>
          ) : (
            parts.map((p) => (
              <TableRow key={p.id}>
                <TableCell className={COMPACT_CELL}>
                  <input
                    type="color"
                    aria-label={`Color de ${p.label}`}
                    className="h-8 w-8 cursor-pointer rounded border border-slate-200"
                    value={p.color}
                    onChange={(e) => onUpdate(p.id, { color: e.target.value })}
                  />
                </TableCell>
                <TableCell className={COMPACT_CELL}>
                  <Input
                    aria-label={`Nombre de la pieza ${p.id}`}
                    className={NAME_INPUT}
                    value={p.label}
                    onChange={(e) => onUpdate(p.id, { label: e.target.value })}
                  />
                </TableCell>
                <TableCell className={COMPACT_CELL}>
                  <Input
                    type="number"
                    min={0.1}
                    step={0.1}
                    aria-label={`Ancho de ${p.label}`}
                    className={NUM_INPUT}
                    value={p.width}
                    onChange={(e) =>
                      onUpdate(p.id, {
                        width: Math.max(0.1, parseFloat(e.target.value) || 0.1),
                      })
                    }
                  />
                </TableCell>
                <TableCell className={COMPACT_CELL}>
                  <Input
                    type="number"
                    min={0.1}
                    step={0.1}
                    aria-label={`Largo de ${p.label}`}
                    className={NUM_INPUT}
                    value={p.height}
                    onChange={(e) =>
                      onUpdate(p.id, {
                        height: Math.max(0.1, parseFloat(e.target.value) || 0.1),
                      })
                    }
                  />
                </TableCell>
                <TableCell className={COMPACT_CELL}>
                  <Input
                    type="number"
                    min={1}
                    step={1}
                    aria-label={`Cantidad de ${p.label}`}
                    className={NUM_INPUT}
                    value={p.quantity}
                    onChange={(e) =>
                      onUpdate(p.id, {
                        quantity: Math.max(1, Math.round(parseFloat(e.target.value) || 1)),
                      })
                    }
                  />
                </TableCell>
                <TableCell className={`${COMPACT_CELL} text-center`}>
                  <input
                    type="checkbox"
                    aria-label={`Permitir rotar ${p.label}`}
                    checked={p.canRotate}
                    onChange={(e) => onUpdate(p.id, { canRotate: e.target.checked })}
                  />
                </TableCell>
                <TableCell className={COMPACT_CELL}>
                  <div className="flex gap-0.5">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      aria-label={`Duplicar ${p.label}`}
                      title="Duplicar"
                      className="h-8 w-8 px-0"
                      onClick={() => onDuplicate(p.id)}
                    >
                      {/* Icono "duplicar" (dos rectángulos superpuestos). */}
                      <svg
                        aria-hidden="true"
                        viewBox="0 0 24 24"
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect x="9" y="9" width="11" height="11" rx="2" />
                        <path d="M5 15V5a2 2 0 0 1 2-2h10" />
                      </svg>
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      aria-label={`Eliminar ${p.label}`}
                      title="Eliminar"
                      className="h-8 w-8 px-0 text-red-600 hover:bg-red-50"
                      onClick={() => onRemove(p.id)}
                    >
                      {/* Icono "eliminar" (papelera). */}
                      <svg
                        aria-hidden="true"
                        viewBox="0 0 24 24"
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M3 6h18" />
                        <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                        <path d="M10 11v6M14 11v6" />
                      </svg>
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <div>
        <Button type="button" variant="secondary" size="sm" onClick={onAdd}>
          + Agregar pieza
        </Button>
      </div>
      <p className="text-xs text-slate-500">
        ↻ = la pieza puede girarse 90° si mejora el resultado.
      </p>
    </div>
  );
}
